import React, { useState } from "react";
import CustomScroll from "react-custom-scroll";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink as ReactRouterLink, useNavigate } from "react-router-dom";

import {
	IconButton,
	Avatar,
	Box,
	CloseButton,
	Flex,
	HStack,
	VStack,
	Icon,
	useColorModeValue,
	Link,
	Drawer,
	DrawerContent,
	Text,
	useDisclosure,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	useColorMode,
	Button,
	Progress,
	Spinner,
	useToast,
	Image,
	Stack,
	Select,
	ModalOverlay,
	Modal,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	FormControl,
	FormLabel,
	Input,
	ModalFooter,
	Tooltip,
} from "@chakra-ui/react";
import { AiOutlineAudit } from "react-icons/ai";
import { BiBuildingHouse } from "react-icons/bi";
import { BsSun, BsMoonStarsFill, BsClipboardPlus, BsSpeedometer2 } from "react-icons/bs";
import { CgFileDocument, CgProfile } from "react-icons/cg";
import { FiTrendingUp, FiMenu, FiBell, FiChevronDown, FiRefreshCw } from "react-icons/fi";
import { MdOutlineMessage, MdOutlinePayments, MdOutlineEventRepeat } from "react-icons/md";
import { RiDashboardLine, RiLuggageDepositLine } from "react-icons/ri";
import { TbCalendarTime, TbFileInvoice } from "react-icons/tb";

import { signOutAsync } from "../../redux/auth/authSlice";
import { getDashboardAsync } from "../../redux/user/userSlice";
import { setHeaders, getHeaders, API } from "../../utils/http";
import i18n from "../../utils/i18n";

export default function Navbar({ children }) {
	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<Box minH="100vh" bg={useColorModeValue("gray.50", "gray.900")}>
			<SidebarContent onClose={() => onClose} display={{ base: "none", md: "block" }} />
			<Drawer autoFocus={false} isOpen={isOpen} placement="left" onClose={onClose} returnFocusOnClose={false} onOverlayClick={onClose} size="full">
				<DrawerContent>
					<SidebarContent onClose={onClose} />
				</DrawerContent>
			</Drawer>
			<MobileNav onOpen={onOpen} />
			<Box ml={{ base: 0, md: 60 }} p="6">
				{children}
			</Box>
		</Box>
	);
}

const SidebarContent = ({ onClose, ...rest }) => {
	const accountType = useSelector((state) => state.user.user?.type);
	const { t } = useTranslation();
	const TenantLinkItems = [
		{ name: t("navbarMenu.dashboard"), icon: RiDashboardLine, path: "/dashboard" },
		{ name: t("navbarMenu.utilities"), icon: BiBuildingHouse, path: "/utilities" },
		{ name: t("navbarMenu.payments"), icon: MdOutlinePayments, path: "/payments" },
		{ name: t("navbarMenu.documents"), icon: CgFileDocument, path: "/documents" },
		{ name: t("navbarMenu.tickets"), icon: BsClipboardPlus, path: "/tickets" },
		{ name: t("navbarMenu.messages"), icon: MdOutlineMessage, path: "/messages" },
	];
	const AgentLinkItems = [
		{ name: t("navbarMenu.dashboard"), icon: RiDashboardLine, path: "/dashboard" },
		{ name: t("navbarMenu.properties"), icon: BiBuildingHouse, path: "/properties" },
		{ name: t("navbarMenu.outstanding"), icon: MdOutlinePayments, path: "/outstanding" },
		{ name: t("navbarMenu.documents"), icon: TbFileInvoice, path: "/documents" },
		{ name: t("navbarMenu.utilities"), icon: BsSpeedometer2, path: "/utilities" },
		{ name: t("navbarMenu.financialStatus"), icon: FiTrendingUp, path: "/financial-status" },
		{ name: t("navbarMenu.statements"), icon: AiOutlineAudit, path: "/statements" },
		{ name: t("navbarMenu.notifications"), icon: FiBell, path: "/mass-notification" },
		{ name: t("navbarMenu.tickets"), icon: BsClipboardPlus, path: "/tickets" },
		{ name: t("navbarMenu.securityDeposits"), icon: RiLuggageDepositLine, path: "/security-deposits" },
		{ name: t("navbarMenu.profile"), icon: CgProfile, path: "/profile" },
	];
	const AgencyLinkItems = [...AgentLinkItems, { name: t("navbarMenu.subscription"), icon: TbCalendarTime, path: "/subscription" }];
	const OwnerLinkItems = [
		{ name: t("navbarMenu.dashboard"), icon: RiDashboardLine, path: "/dashboard" },
		{ name: t("navbarMenu.payments"), icon: MdOutlinePayments, path: "/payments" },
		{ name: t("navbarMenu.financialStatus"), icon: FiTrendingUp, path: "/financial-status" },
		{ name: t("navbarMenu.documents"), icon: CgFileDocument, path: "/documents" },
		{ name: t("navbarMenu.tickets"), icon: BsClipboardPlus, path: "/tickets" },
		{ name: t("navbarMenu.messages"), icon: MdOutlineMessage, path: "/messages" },
		{ name: t("navbarMenu.customOwnerPlans"), icon: MdOutlineEventRepeat, path: "/custom-owner-plans" },
		{ name: t("navbarMenu.statements"), icon: AiOutlineAudit, path: "/financial-statements" },
	];
	const LinkItems = {
		owner: OwnerLinkItems,
		tenant: TenantLinkItems,
		agent: AgentLinkItems,
		agency: AgencyLinkItems,
	};
	return (
		<Box
			// transition="all 0.2s ease-in-out"
			bg={useColorModeValue("white", "gray.900")}
			borderRight="1px"
			borderRightColor={useColorModeValue("gray.200", "gray.700")}
			w={{ base: "full", md: 60 }}
			pos="fixed"
			h="full"
			shadow="sm"
			{...rest}
		>
			<CustomScroll heightRelativeToParent="100%">
				<Flex h="20" alignItems="center" mx="6" justifyContent="space-between">
					<Stack direction={"row"} height="100%" alignItems="center" spacing={2}>
						<Image src="https://rentit-images.s3.eu-central-1.amazonaws.com/logo.png" maxH="45px" />
					</Stack>
					<CloseButton display={{ base: "flex", md: "none" }} onClick={onClose} />
				</Flex>

				<VStack spacing={1} align="stretch" mt={2}>
					{LinkItems[accountType]?.map((link) => (
						<NavItem key={link.name} icon={link.icon} path={link.path} onClick={onClose}>
							{link.name}
						</NavItem>
					))}
				</VStack>
			</CustomScroll>
		</Box>
	);
};

const NavItem = ({ icon, path, children, ...rest }) => {
	const bgColor = useColorModeValue("gray.100", "gray.700");
	const textColor = useColorModeValue("gray.600", "gray.300");
	const hoverTextColor = useColorModeValue("gray.900", "white");

	return (
		<Link
			as={ReactRouterLink}
			to={path}
			style={{
				display: "block",
				textDecoration: "none",
			}}
			_focus={{ boxShadow: "none" }}
		>
			{({ isActive }) => {
				return (
					<Flex
						align="center"
						p="3"
						mx="3"
						borderRadius="md"
						role="group"
						cursor="pointer"
						// transition="all 0.2s"
						bg={isActive ? "teal.500" : "transparent"}
						color={isActive ? "white" : textColor}
						_hover={{
							bg: isActive ? "teal.600" : bgColor,
							color: isActive ? "white" : hoverTextColor,
						}}
						{...rest}
					>
						{icon && <Icon mr="4" fontSize="18" as={icon} />}
						<Text fontSize="md" fontWeight="medium">
							{children}
						</Text>
					</Flex>
				);
			}}
		</Link>
	);
};

const MobileNav = ({ onOpen, ...rest }) => {
	const { colorMode, toggleColorMode } = useColorMode();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const handleSignOut = async () => {
		dispatch(signOutAsync());
	};
	const status = useSelector((state) => state.user.status);
	const currentUserEmail = useSelector((state) => state.auth.currentUserEmail);
	const accountType = useSelector((state) => state.user.user?.type);
	const toast = useToast();
	const handleChangeLanguage = (e) => {
		console.log(e.target.value);
		i18n.changeLanguage(e.target.value);
	};
	const refreshDashboard = () => {
		dispatch(getDashboardAsync()).then((r) => {
			console.log(r);
			if (r.error?.message)
				toast({
					title: "Error encountered",
					description: r.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			else {
				toast({
					title: "Success!",
					description: "Dashboard info success",
					status: "success",
					duration: 2000,
					isClosable: true,
				});
			}
		});
	};
	const [onBehalfOf, setOnBehalfOf] = useState("");
	const handleChangeOnBehalfOf = (e) => {
		setOnBehalfOf(e.target.value);
	};

	const saveOnBehalfOf = () => {
		setHeaders({ ...getHeaders(), onbehalfof: onBehalfOf });
		dispatch(getDashboardAsync())
			.then((r) => {
				console.log(r);
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Dashboard info success",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				navigate(`/`);
				onBehalfOfClose();
			});
	};
	const { isOpen: isOnBehalfOfOpen, onOpen: onOnBehalfOfOpen, onClose: onBehalfOfClose } = useDisclosure();

	const setRenderOnBehalfOfModal = () => (
		<Modal isOpen={isOnBehalfOfOpen} onClose={onBehalfOfClose} size="lg" isCentered>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.100">
					<Text fontSize="xl">Set On Behalf Of</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<FormControl>
						<FormLabel>Email Address</FormLabel>
						<Input type="email" placeholder="Enter email address" value={onBehalfOf} onChange={handleChangeOnBehalfOf} />
					</FormControl>
					<Button onClick={() => setOnBehalfOf("office@paltinul.ro")} variant="outline" colorScheme="blue" size="sm" mt={2}>
						office@paltinul.ro
					</Button>
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button variant="ghost" onClick={onBehalfOfClose} mr={3}>
						Cancel
					</Button>
					<Button colorScheme="blue" onClick={saveOnBehalfOf}>
						Save Changes
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	const [changeEmail, setChangeEmail] = useState({
		fromEmail: "",
		toEmail: "",
	});
	const handleChangeChangeEmail = (e) => {
		setChangeEmail({
			...changeEmail,
			[e.target.name]: e.target.value,
		});
	};

	const saveChangeEmail = () => {
		console.log(changeEmail);
		API.put("/change-email", changeEmail)
			.then((r) => {
				console.log(r);
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Email change success",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
					setChangeEmail({
						fromEmail: "",
						toEmail: "",
					});
				}
			})
			.catch((e) => {
				toast({
					title: "Error encountered",
					description: e.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};
	const { isOpen: isChangeEmailOpen, onOpen: onChangeEmailOpen, onClose: onChangeEmailClose } = useDisclosure();

	const renderChangeEmailModal = () => (
		<Modal isOpen={isChangeEmailOpen} onClose={onChangeEmailClose} size={"xl"}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>
					<Text>Change email</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<FormControl>
						<FormLabel display={"inline"}>From email</FormLabel>
						<Input
							width={"fit-content"}
							type="text"
							name="fromEmail"
							_placeholder={{ color: "gray.500" }}
							value={changeEmail.fromEmail}
							onChange={handleChangeChangeEmail}
						/>
					</FormControl>
					<FormControl>
						<FormLabel display={"inline"}>To email</FormLabel>
						<Input
							width={"fit-content"}
							type="text"
							name="toEmail"
							_placeholder={{ color: "gray.500" }}
							value={changeEmail.toEmail}
							onChange={handleChangeChangeEmail}
						/>
					</FormControl>
				</ModalBody>
				<ModalFooter>
					<Button colorScheme="red" onClick={onChangeEmailClose} mr={3}>
						Cancel
					</Button>
					<Button colorScheme="blue" onClick={saveChangeEmail}>
						Save
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	return (
		<>
			<Flex
				ml={{ base: 0, md: 60 }}
				px={{ base: 4, md: 6 }}
				height="20"
				alignItems="center"
				bg={useColorModeValue("white", "gray.900")}
				borderBottomWidth="1px"
				borderBottomColor={useColorModeValue("gray.200", "gray.700")}
				justifyContent={{ base: "space-between", md: "flex-end" }}
				shadow="sm"
				{...rest}
			>
				<IconButton display={{ base: "flex", md: "none" }} onClick={onOpen} variant="ghost" aria-label="open menu" icon={<FiMenu />} size="lg" />

				<Image display={{ base: "flex", md: "none" }} src="https://rentit-images.s3.eu-central-1.amazonaws.com/logo.png" maxH="45px" />

				<HStack spacing={{ base: "2", md: "4" }}>
					<Tooltip label="Refresh dashboard">
						<IconButton
							variant="ghost"
							aria-label="Refresh dashboard"
							icon={status === "loading" ? <Spinner size="sm" /> : <FiRefreshCw />}
							onClick={refreshDashboard}
							size="md"
						/>
					</Tooltip>

					<Tooltip label="Toggle color mode">
						<IconButton
							variant="ghost"
							aria-label="Toggle Color Mode"
							icon={colorMode === "light" ? <BsMoonStarsFill /> : <BsSun />}
							onClick={toggleColorMode}
							size="md"
						/>
					</Tooltip>

					<Select onChange={handleChangeLanguage} width="80px" size="sm" variant="filled" borderRadius="md">
						<option value="ro">RO</option>
						<option value="en">EN</option>
					</Select>

					<Flex alignItems="center">
						{setRenderOnBehalfOfModal()}
						{renderChangeEmailModal()}
						<Menu>
							<MenuButton
								py={2}
								// transition="all 0.3s"
								_focus={{ boxShadow: "none" }}
								_hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
								borderRadius="md"
								px={3}
							>
								<HStack spacing={3}>
									<Avatar size="sm" src={`https://ui-avatars.com/api/?name=${currentUserEmail}&length=1`} bg="teal.500" />
									<VStack display={{ base: "none", md: "flex" }} alignItems="flex-start" spacing="1px" ml="2">
										<Text fontSize="sm" fontWeight="medium">
											{currentUserEmail}
										</Text>
										<Text fontSize="xs" color={useColorModeValue("gray.600", "gray.400")}>
											{accountType}
										</Text>
									</VStack>
									<Box display={{ base: "none", md: "flex" }}>
										<FiChevronDown />
									</Box>
								</HStack>
							</MenuButton>
							<MenuList
								bg={useColorModeValue("white", "gray.900")}
								borderColor={useColorModeValue("gray.200", "gray.700")}
								shadow="lg"
								borderRadius="md"
								py={2}
							>
								<MenuItem hidden={currentUserEmail !== "omihai00@gmail.com"} icon={<Icon as={CgProfile} />} onClick={onOnBehalfOfOpen}>
									Set on behalf of
								</MenuItem>
								<MenuItem hidden={currentUserEmail !== "omihai00@gmail.com"} icon={<Icon as={MdOutlineMessage} />} onClick={onChangeEmailOpen}>
									Change email
								</MenuItem>
								<MenuItem hidden={accountType === "owner" || accountType === "tenant"} icon={<Icon as={CgProfile} />} onClick={() => navigate("/profile")}>
									Profile
								</MenuItem>
								<MenuDivider />
								<MenuItem icon={<Icon as={FiMenu} />} onClick={handleSignOut}>
									Sign out
								</MenuItem>
							</MenuList>
						</Menu>
					</Flex>
				</HStack>
			</Flex>
			<Progress
				size="xs"
				isIndeterminate
				hidden={status === "idle"}
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					width: "100%",
					zIndex: 9999,
				}}
			/>
		</>
	);
};
